import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { useAuth, hasAuthParams } from 'react-oidc-context'
import { Navigate, useLocation } from 'react-router-dom'

import { setRedirectPath } from '../api'

import { backendApi } from '@/stores/backendApi'
import { sfApi } from '@/stores/sfApi'

/**
 * 案件起点 (offer-driven) frontend が `/login` 遷移前に書く return-to cookie。
 * OIDC 完了後、本 AuthGuard が `<p>Loading...</p>` を出している間に **本 cookie が
 * 立っている場合だけ** 案件起点 SPA へ離脱させるための補助ロジック。
 *
 * 重要: cookie が無い (= recommend 単独ユーザー) ケースでは early return し、
 * 既存の `<Navigate to={redirectPath}>` (Callback.jsx) による recommend 内
 * in-app navigation はそのまま走る。**recommend 通常 flow には一切影響しない**。
 *
 * 案件起点側 (`apps/web/src/features/auth/return-to.ts`) と仕様を揃える:
 *   - cookie 名: `offer_driven_return_to`
 *   - 値:        案件起点 SPA の **実在する route** のみ (allowlist exact match)
 *   - 削除属性:  `Path=/; SameSite=Lax; Secure(HTTPS のみ); Max-Age=0` (保存時と一致)
 *
 * `/offer-driven/` prefix の broad allow ではなく **完全一致 (pathname のみ)** で
 * 判定する理由: prefix allow だと `/offer-driven/foo` のような未定義 path も通過し、
 * 認証後 consume で `window.location.replace("/offer-driven/foo")` → 案件起点 SPA
 * reload → React Router fallback で `/` に Navigate、というループ / 誤遷移を
 * 起こし得る。実 route 完全一致なら cookie には常に実在画面の path のみが入る。
 *
 * 案件起点 SPA に新 route を追加する場合は本配列を同期する必要がある
 * (`tryt-offer-driven` の `apps/web/src/routes/app-routes.tsx` 参照)。
 */
const OFFER_DRIVEN_RETURN_TO_COOKIE = 'offer_driven_return_to'
const OFFER_DRIVEN_ALLOWED_PATHS = ['/offer-driven/reactions']

function readOfferDrivenReturnTo() {
    const match = document.cookie.match(
        /(?:^|;\s*)offer_driven_return_to=([^;]+)/,
    )
    if (!match) return null
    try {
        return decodeURIComponent(match[1])
    } catch {
        return null
    }
}

function clearOfferDrivenReturnTo() {
    // attribute は保存時 (案件起点側 saveReturnTo) と一致させる必要がある。
    // `Secure` 属性は HTTPS 文脈以外では cookie 自体が silent reject されるため、
    // dev (HTTP) では外す。`SameSite=Lax` は HTTPS 非依存。
    const isHttps =
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:'
    const secure = isHttps ? '; Secure' : ''
    document.cookie = `${OFFER_DRIVEN_RETURN_TO_COOKIE}=; Path=/; SameSite=Lax${secure}; Max-Age=0`
}

function isSafeOfferDrivenReturnTo(value) {
    if (typeof value !== 'string' || value.length === 0) return false
    if (!value.startsWith('/')) return false
    // `//` (protocol-relative) や `/\` (一部ブラウザで `//` 同等扱い) は
    // 絶対 URL 解釈になり得るので拒否
    if (value.startsWith('//')) return false
    if (value.startsWith('/\\')) return false
    // pathname (query / hash を除いた部分) を allowlist と exact 比較
    const queryIndex = value.indexOf('?')
    const hashIndex = value.indexOf('#')
    let endIndex = value.length
    if (queryIndex !== -1) endIndex = Math.min(endIndex, queryIndex)
    if (hashIndex !== -1) endIndex = Math.min(endIndex, hashIndex)
    const pathname = value.slice(0, endIndex)
    return OFFER_DRIVEN_ALLOWED_PATHS.includes(pathname)
}

const AuthGuard = ({ children }) => {
    const location = useLocation()
    const auth = useAuth()
    const getUser = sfApi.useGetMeQuery(undefined, {
        skip: !auth.isAuthenticated,
    })
    const getAdmin = backendApi.useGetMyAdminInfoQuery(undefined, {
        skip: !auth.isAuthenticated,
    })

    console.log({ auth, admin: getAdmin, user: getUser })

    // 案件起点 (offer-driven) frontend の return-to cookie 復帰。
    // **cookie が立っている時だけ** 案件起点 URL へ full-reload する。cookie が
    // 無い recommend 通常ユーザーは early return → 既存 flow が完全に維持される。
    const offerDrivenReturnHandledRef = useRef(false)
    useEffect(() => {
        if (!auth.isAuthenticated) return
        if (offerDrivenReturnHandledRef.current) return
        offerDrivenReturnHandledRef.current = true
        const returnTo = readOfferDrivenReturnTo()
        if (!returnTo) return
        clearOfferDrivenReturnTo()
        if (!isSafeOfferDrivenReturnTo(returnTo)) return
        const here =
            window.location.pathname +
            window.location.search +
            window.location.hash
        if (returnTo === here) return
        window.location.replace(returnTo)
    }, [auth.isAuthenticated])

    useEffect(() => {
        auth.events.addUserLoaded((/* user */) => {
            getAdmin.refetch()
            getUser.refetch()
        })
    }, [auth.events, getAdmin.refetch, getUser.refetch])

    // automatically sign-in
    useEffect(() => {
        if (
            !hasAuthParams() &&
            !auth.isAuthenticated &&
            !auth.activeNavigator &&
            !auth.isLoading
        ) {
            setRedirectPath(location.pathname + location.search)
            auth.signinRedirect()
        }
    }, [
        auth.isAuthenticated,
        auth.activeNavigator,
        auth.isLoading,
        auth.signinRedirect,
    ])

    if (getUser.data && getAdmin.isSuccess) {
        return <>{children}</>
    }

    if (auth.error) {
        return (
            <Navigate
                to="/error"
                state={{
                    code: 401,
                    message: 'Unable to log in',
                    error: auth.error,
                }}
            />
        )
    }

    return <p>Loading...</p>
}

AuthGuard.propTypes = {
    children: PropTypes.node.isRequired,
}

export { AuthGuard }
